"use client";

import { useQuery } from "@tanstack/react-query";
import {
  getCurrentShellProfile,
  type ProfileReadErrorCode,
} from "@/actions/profile-shell";
import { useAuthState } from "@/hooks/use-auth-state";

export const SHELL_PROFILE_QUERY_KEY = ["profile", "shell"] as const;
export const SHELL_PROFILE_STALE_TIME_MS = 5 * 60 * 1000;

function shouldResolveAsEmpty(code: ProfileReadErrorCode) {
  return code === "UNAUTHENTICATED" || code === "PROFILE_NOT_FOUND";
}

/**
 * Exported so imperative readers (`queryClient.fetchQuery`) share this exact
 * function. Two query functions under one key diverge on the error path, and
 * whichever ran last decides what the shell sees.
 */
export async function fetchShellProfile() {
  const result = await getCurrentShellProfile();
  if (result.ok) return result.data;
  if (shouldResolveAsEmpty(result.code)) return null;
  throw new Error(result.message);
}

export function useShellProfileData() {
  const { authStatus, isError: isAuthError } = useAuthState();
  const shouldFetch = authStatus === "authenticated";
  const query = useQuery({
    queryKey: SHELL_PROFILE_QUERY_KEY,
    queryFn: fetchShellProfile,
    enabled: shouldFetch,
    staleTime: SHELL_PROFILE_STALE_TIME_MS,
  });

  const isResolving =
    (authStatus === "unknown" && !isAuthError) ||
    (shouldFetch && query.isPending);

  return {
    ...query,
    data: shouldFetch ? query.data : null,
    isLoading: isResolving,
    isPending: isResolving,
  };
}
